import React from 'react';
import { Button } from '@/components/ui/button';

import type { ProgramControlCommonProps, SubmitReportFn } from '../controlTypes';

type CovidTestingProgramControlsProps = ProgramControlCommonProps & {
  covidTestingProgramOptions: string[];
  onSubmitReport: SubmitReportFn;
};

export const CovidTestingProgramControls = ({
  values,
  onFilterChange,
  actionRowClass,
  renderStandardActionButtons,
  renderReportActionLabel,
  covidTestingProgramOptions,
  onSubmitReport,
}: CovidTestingProgramControlsProps) => (
  <div className="mb-3 flex flex-wrap items-end justify-between gap-4 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-white px-4 py-3">
    <div className="flex flex-wrap items-center gap-6">
      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Type</div>
        <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--neutral-gray-120)]">
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-type" checked={values.covidTestingTypeFilter === 'all'} onChange={() => onFilterChange('covidTestingTypeFilter', 'all')} />All</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-type" checked={values.covidTestingTypeFilter === 'Admission'} onChange={() => onFilterChange('covidTestingTypeFilter', 'Admission')} />Admission</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-type" checked={values.covidTestingTypeFilter === 'Diagnostic'} onChange={() => onFilterChange('covidTestingTypeFilter', 'Diagnostic')} />Diagnostic</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-type" checked={values.covidTestingTypeFilter === 'Outbreak'} onChange={() => onFilterChange('covidTestingTypeFilter', 'Outbreak')} />Outbreak</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-type" checked={values.covidTestingTypeFilter === 'Surveillance'} onChange={() => onFilterChange('covidTestingTypeFilter', 'Surveillance')} />Surveillance</label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Source</div>
        <div className="flex items-center gap-4 text-xs text-[var(--neutral-gray-120)]">
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-source" checked={values.covidTestingSourceFilter === 'all'} onChange={() => onFilterChange('covidTestingSourceFilter', 'all')} />All</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-source" checked={values.covidTestingSourceFilter === 'staff'} onChange={() => onFilterChange('covidTestingSourceFilter', 'staff')} />Staff</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-source" checked={values.covidTestingSourceFilter === 'client'} onChange={() => onFilterChange('covidTestingSourceFilter', 'client')} />Client</label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Program</div>
        <select
          value={values.covidTestingProgramFilter}
          onChange={(event) => onFilterChange('covidTestingProgramFilter', String(event.target.value || 'all'))}
          className="h-8 min-w-[170px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
        >
          <option value="all">All</option>
          {covidTestingProgramOptions.map((program) => (
            <option key={program} value={program}>{program}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Result</div>
        <div className="flex items-center gap-4 text-xs text-[var(--neutral-gray-120)]">
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-result" checked={values.covidTestingResultFilter === 'all'} onChange={() => onFilterChange('covidTestingResultFilter', 'all')} />All</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-result" checked={values.covidTestingResultFilter === 'Positive'} onChange={() => onFilterChange('covidTestingResultFilter', 'Positive')} />Positive</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-result" checked={values.covidTestingResultFilter === 'Negative'} onChange={() => onFilterChange('covidTestingResultFilter', 'Negative')} />Negative</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-result" checked={values.covidTestingResultFilter === 'Inconclusive'} onChange={() => onFilterChange('covidTestingResultFilter', 'Inconclusive')} />Inconclusive</label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Status</div>
        <div className="flex items-center gap-4 text-xs text-[var(--neutral-gray-120)]">
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-closed" checked={values.covidTestingClosedFilter === 'all'} onChange={() => onFilterChange('covidTestingClosedFilter', 'all')} />All</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-closed" checked={values.covidTestingClosedFilter === 'n'} onChange={() => onFilterChange('covidTestingClosedFilter', 'n')} />Open</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="covid-testing-closed" checked={values.covidTestingClosedFilter === 'y'} onChange={() => onFilterChange('covidTestingClosedFilter', 'y')} />Closed</label>
        </div>
      </div>
    </div>

    <div className={actionRowClass}>
      {renderStandardActionButtons()}
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onSubmitReport({ action: 'all-fields-report', download: '1' })}>
        {renderReportActionLabel('+ All Fields Report')}
      </Button>
    </div>
  </div>
);

